import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { usePAARegistration } from '@src/state/PAARegistration';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { useTIN } from '@src/state/taxInformation';
import { useEditing } from '@src/state/editing/hooks';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useOnloading } from '@src/state/landing';
import { getOrgType } from '../helpers';
import { useUser } from '@src/state/user';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '10px',
  fontFamily: '$primary',
});

const TooltipText = styled('p', {
  fontSize: '16px',
  fontWeight: 600,
  marginBottom: '10px',
  lineHeight: '1.3',
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const PAAToolTip = styled('span', {
  color: '$info1',
  display: 'inline-block',
  paddingBottom: '1px',
  borderBottom: '1px dashed $info1',
  fontWeight: 400,
  '&:hover': {
    cursor: 'pointer',
  },
});

const PaaToolTipText = styled('div', {
  display: 'inline-block',
});

export const AccessAdministrator = ({ handleBack, handleNext }) => {
  const form = useForm();
  const { setSelfAttestationDone } = usePAARegistration.actions();
  const { attested } = usePAARegistration.state();
  const { TINNumber, TINInfo } = useTIN.state();
  const { isEditing } = useEditing.state();
  const [selfAttestationCheck, setSelfAttestationCheck] = useState(attested);
  const [trueInformationCheck, setTrueInformationCheck] = useState(attested);
  const [responsibilityCheck, setResponsibilityCheck] = useState(attested);
  const [selfAttestationError, setSelfAttestationError] = useState(false);
  const [entityAttestationCheck, setEntityAttestationCheck] =
    useState(attested);
  const tinInfo = TINInfo == null ? { corpName: 'ABC Hopsital' } : TINInfo;
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { currentFlow } = useOnloading.state();

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  useEffect(() => {
    form.setValue('PAASelfAttestation.selfAttestationCheck', attested);
    form.setValue('PAASelfAttestation.trueInformationCheck', attested);
    form.setValue('PAASelfAttestation.responsibilityCheck', attested);
    form.setValue('PAASelfAttestation.entityAttestationCheck', attested);

    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'paa registration';
      const section2 = 'admin attestation';
      const pageName = `digitalsecurity:${section1}:${section2}`;

      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: tinInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3: '',
      });
    }
  }, []);

  useEffect(() => {
    if (selfAttestationCheck && trueInformationCheck && responsibilityCheck) {
      setSelfAttestationError(null);
    }
  }, [selfAttestationCheck, trueInformationCheck, responsibilityCheck]);

  const handleSubmit = (e) => {
    form.validate('PAASelfAttestation.selfAttestationCheck');
    form.validate('PAASelfAttestation.trueInformationCheck');
    form.validate('PAASelfAttestation.responsibilityCheck');
    form.validate('PAASelfAttestation.entityAttestationCheck');
    setSelfAttestationError(null);

    if (
      !selfAttestationCheck ||
      !trueInformationCheck ||
      !responsibilityCheck ||
      (isAPIMarketPlaceUser && !entityAttestationCheck)
    ) {
      setSelfAttestationError(
        'Error: All checkboxes are required in order to proceed'
      );
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const fields = ['PAA Self Attestation'];
        adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
      }

      return;
    }
    setSelfAttestationDone({ attested: true });
    handleNext();
  };
  const getTitleText = currentFlow.includes('technician')
    ? 'Attest to your role as the API Consumer. '
    : `Attest to your role as the Secret Keeper.`;
  const getTooltipBoldText = currentFlow.includes('technician')
    ? `API Consumer`
    : `Secret Keeper`;
  const getTooltipText = currentFlow.includes('technician')
    ? ` can view your organization's APIs on API Marketplace and request additional APIs.`
    : ` functions as the primary technical link between your organization and API Marketplace.
    Secret Keeper is solely responsible for maintaining your organization’s API credentials and generating new API credentials, as needed. They will have the ability to view your organizations APIs on API Marketplace and request additional APIs.`;
  const getResponsibilityText = currentFlow.includes('technician')
    ? `I attest taking on the responsibility as the API Consumer for my organization.`
    : `I attest taking on the responsibility as the Secret Keeper for my organization.`;

  const errorBorder = selfAttestationError ? '2px solid rgb(208 2 28)' : 'none';

  const NextButton = styled('div', {
    float: 'right',
    width: '115px',
    padding: '6px',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    borderWidth: '2.5px',
    border: '2px solid $primary1',
    background: '$primary1',
    borderStyle: 'solid',
    borderRadius: '23px',
    marginTop: `${isEditing ? 'auto' : '-60px'}`,
    marginBottom: '20px',
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
  });
  return (
    <React.Fragment>
      <MainContainer>
        <BoxHeader>
          <BoxHeaderText>
            {!isAPIMarketPlaceUser
              ? `Attest your role as the Primary Access Administrator.`
              : getTitleText}
          </BoxHeaderText>
        </BoxHeader>
        <Description>
          Please review and select all to attest that you are the{' '}
          <PaaToolTipText>
            <Tooltip
              id="tooltip"
              placement="bottom"
              aria-label="tooltip label"
              aria-describedby="tooltip described by"
              width={500}
              asChild={false}
              content={
                <React.Fragment>
                  {!isAPIMarketPlaceUser ? (
                    <TooltipText>
                      <strong>Primary Access Administrator (PAA)</strong>{' '}
                      functions as the primary link between your organization
                      and UnitedHealthCare.
                    </TooltipText>
                  ) : (
                    <TooltipText>
                      {currentFlow.includes('technician') ? 'An ' : 'The '}
                      <strong>{getTooltipBoldText}</strong> {getTooltipText}
                    </TooltipText>
                  )}
                  <TooltipText>
                    {!isAPIMarketPlaceUser
                      ? `Your PAA is responsible for managing security, setting up and maintaining user accounts, updating organization information, and performing other administrative tasks in the Portal.`
                      : null}
                  </TooltipText>
                </React.Fragment>
              }
            >
              <PAAToolTip>
                {!isAPIMarketPlaceUser
                  ? 'Primary Access Administrator.'
                  : getTooltipBoldText}
              </PAAToolTip>{' '}
            </Tooltip>{' '}
          </PaaToolTipText>
        </Description>
        <div
          style={{
            marginBottom: '5px',
            marginTop: '5px',
            padding: '5px',
            border: errorBorder,
            borderRadius: '4px',
          }}
        >
          <FormProvider state={form} style={{ marginBottom: '20px' }} required>
            <Checkbox
              data-testid="selfCheck"
              id="selfCheck"
              model="PAASelfAttestation.selfAttestationCheck"
              size="sm"
              label={`I attest that I am submitting on my own behalf and I am the
                  user associated with this Organization TIN; or I have the
                  authority to submit a request on behalf of the group(s)
                  associated with this ${tinInfo.corpName} tax ID number:
                  ${TINNumber}.`}
              onChange={(e) => {
                setSelfAttestationCheck(e.target.checked);
                form.validate('PAASelfAttestation.selfAttestationCheck');
              }}
              aria-label=""
              css={{
                'abyss-checkbox-label': {
                  fontWeight: 'normal',
                },
              }}
            />
          </FormProvider>
          <FormProvider state={form} style={{ marginBottom: '20px' }}>
            <Checkbox
              data-testid="trueCheck"
              id="trueCheck"
              model="PAASelfAttestation.trueInformationCheck"
              size="sm"
              label="I certify that all information provided as part of this
                  process are true, accurate and complete, to the best of my
                  knowledge."
              onChange={(e) => {
                setTrueInformationCheck(e.target.checked);
                form.validate('PAASelfAttestation.trueInformationCheck');
              }}
              aria-label=""
              css={{
                'abyss-checkbox-label': {
                  fontWeight: 'normal',
                },
              }}
            />
          </FormProvider>
          <FormProvider state={form} required>
            <Checkbox
              data-testid="responsibilityCheck"
              id="responsibilityCheck"
              model="PAASelfAttestation.responsibilityCheck"
              label={
                !isAPIMarketPlaceUser
                  ? `I attest taking on the responsibility as the Primary Access Administrator for my organization.`
                  : getResponsibilityText
              }
              size="sm"
              onChange={(e) => {
                setResponsibilityCheck(e.target.checked);
                form.validate('PAASelfAttestation.responsibilityCheck');
              }}
              aria-label=""
              css={{
                'abyss-checkbox-label': {
                  fontWeight: 'normal',
                },
              }}
            />
          </FormProvider>
          {isAPIMarketPlaceUser && (
            <FormProvider state={form} style={{ marginTop: '20px' }} required>
              <Checkbox
                data-testid="entityCheck"
                id="entityCheck"
                model="PAASelfAttestation.entityAttestationCheck"
                size="sm"
                onChange={(e) => {
                  setEntityAttestationCheck(e.target.checked);
                  form.validate('PAASelfAttestation.entityAttestationCheck');
                }}
                label={
                  <div>
                    <span style={{ fontWeight: 'normal' }}>
                      Please confirm one of the following to be true:
                    </span>
                    <ul
                      style={{
                        listStyle: 'disc',
                        marginLeft: 18,
                        paddingLeft: 18,
                      }}
                    >
                      <li>Trading Partner is a Covered Entity</li>
                      <li>
                        Trading Partner is a Business Associate of a Covered
                        Entity
                      </li>
                      <li>
                        Trading Partner is both a Covered Entity as well as a
                        Business Associate of a Covered Entity
                      </li>
                      <li>
                        Trading Partner is a sub-business associate to an entity
                        that holds a business associate agreement with a Covered
                        Entity
                      </li>
                    </ul>
                  </div>
                }
                css={{
                  'abyss-checkbox-label': {
                    fontWeight: 'normal',
                  },
                }}
              />
            </FormProvider>
          )}
        </div>
        <p
          style={{ color: 'rgb(208 2 28)', fontSize: '80%', fontWeight: '600' }}
        >
          {selfAttestationError}
        </p>
      </MainContainer>
      <Flex direction="column" justify="space-between">
        <Flex.Content>
          {!isEditing && (
            <BackButton onClick={handleBack}>
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          )}
        </Flex.Content>
        <Flex.Content>
          <NextButton onClick={handleSubmit}>
            <NextButtonText>Next</NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </Flex.Content>
      </Flex>
    </React.Fragment>
  );
};

AccessAdministrator.propTypes = {
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};
